import type { ChangeEvent, FC } from 'react';

import { cn } from '@/utils/cn.util';
import type { ModerationTab, ReportStatus } from '@/types/moderation.types';

import { MODERATION_TABS, REPORT_STATUS_FILTERS, SHOUTT_TABS } from '../constants';

interface ModerationToolbarProps {
  tab: ModerationTab;
  search: string;
  campusId: string;
  reportStatus: ReportStatus | '';
  campuses: { id: string; name: string }[];
  reportedCount: number;
  openReportsCount: number;
  aiFlaggedCount: number;
  onChangeTab: (tab: ModerationTab) => void;
  onChangeSearch: (value: string) => void;
  onChangeCampus: (campusId: string) => void;
  onChangeReportStatus: (status: ReportStatus | '') => void;
}

const selectStyles =
  'h-8 cursor-pointer appearance-none rounded-lg border border-[var(--color-divider)] bg-white px-3 pr-8 text-[12.5px] font-medium text-[var(--color-ink-muted)] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';

const ModerationToolbar: FC<ModerationToolbarProps> = ({
  tab,
  search,
  campusId,
  reportStatus,
  campuses,
  reportedCount,
  openReportsCount,
  aiFlaggedCount,
  onChangeTab,
  onChangeSearch,
  onChangeCampus,
  onChangeReportStatus,
}) => {
  const isShouttTab = SHOUTT_TABS.includes(tab);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => onChangeSearch(e.target.value);
  const handleCampus = (e: ChangeEvent<HTMLSelectElement>): void => onChangeCampus(e.target.value);
  const handleReportStatus = (e: ChangeEvent<HTMLSelectElement>): void =>
    onChangeReportStatus(e.target.value as ReportStatus | '');

  const badge = (key: ModerationTab): string => {
    if (key === 'ai_flagged' && aiFlaggedCount > 0) return ` (${aiFlaggedCount})`;
    if (key === 'reported' && reportedCount > 0) return ` (${reportedCount})`;
    if (key === 'reports' && openReportsCount > 0) return ` (${openReportsCount})`;
    return '';
  };

  return (
    <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      {/* Segmented tabs — ported from the prototype's .tabs / .tab styles */}
      <div className="flex flex-wrap gap-1 rounded-[10px] bg-[var(--color-surface-muted)] p-1">
        {MODERATION_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => onChangeTab(t.key)}
            className={cn(
              'cursor-pointer rounded-lg px-4 py-2 text-[12.5px] font-semibold text-[var(--color-ink-muted)] transition-all',
              tab === t.key && 'bg-white text-brand-500 shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
            )}
          >
            {t.label}
            {badge(t.key)}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {isShouttTab ? (
          <>
            <div className="relative w-full sm:w-52">
              <input
                type="search"
                value={search}
                onChange={handleSearch}
                placeholder="🔍 Search post text…"
                className="h-8 w-full rounded-lg border border-[var(--color-divider)] bg-white px-3 text-[12.5px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-soft)] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              />
            </div>
            <div className="relative">
              <select value={campusId} onChange={handleCampus} className={selectStyles}>
                <option value="">🔽 All Campuses</option>
                {campuses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--color-ink-soft)]">
                ▼
              </span>
            </div>
          </>
        ) : tab === 'reports' ? (
          <div className="relative">
            <select value={reportStatus} onChange={handleReportStatus} className={selectStyles}>
              {REPORT_STATUS_FILTERS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--color-ink-soft)]">
              ▼
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ModerationToolbar;
